import React from 'react'
import { useUser } from '@fs/zion-user'
import { wcfInitialState, wcfReducer, ACTIONS } from './WcfReducer'
import useApiRequestor from '../../../shared/helpers/useApiRequestor'
import * as LocationHelper from '../../../shared/helpers/location_Helper'

export const WcfStateContext = React.createContext()
export const WcfDispatchContext = React.createContext()

function WcfProvider({ children }) {
  const [WcfGlobalState, WcfGlobalDispatch] = React.useReducer(wcfReducer, wcfInitialState)
  return (
    <WcfStateContext.Provider value={WcfGlobalState}>
      <WcfDispatchContext.Provider value={WcfGlobalDispatch}>{children}</WcfDispatchContext.Provider>
    </WcfStateContext.Provider>
  )
}

function useWcfState() {
  const context = React.useContext(WcfStateContext)
  if (context === undefined) {
    throw new Error('ERROR: useWcfState must be used inside of WcfProvider')
  }
  return context
}

function useWcfDispatch() {
  const context = React.useContext(WcfDispatchContext)
  if (context === undefined) {
    throw new Error('ERROR: useWcfDispatch must be used inside of WcfProvider')
  }
  return context
}

function useWcfInitialization() {
  // use this hook only once to load required application data on page load
  const user = useUser()
  const dispatch = useWcfDispatch()
  const workingTree = []
  const [makeRequest] = useApiRequestor('get_family', {
    queryStringVars: {
      generations: 8,
    },
  })

  const dispatchInitialTreelessStateUpdate = (noTreeDefaultRedirect) => {
    dispatch({
      type: ACTIONS.UPDATE_INITIAL_STATE,
      data: {
        noTreeDefaultRedirect,
        hasMappableTree: false,
        isAddTreeFlowShowing: true,
        hasUpdatedInitialState: true,
        tree: [],
        countryPercentages: [],
      },
    })
  }

  const getHeritagePercentage = (tree, locationLookup) => {
    const placeIdList = tree.map((person) => person.latLong.placeId)
    const uniquedIdList = [...new Set(placeIdList)]
    const countryCodeMatchedIds = []

    uniquedIdList.forEach((item) => {
      if (item) {
        let currentCountryCodes
        let countryName
        const cc = locationLookup[item].countryCode
        if (locationLookup[item].currentCountryCodes) {
          countryName = locationLookup[item].name
          currentCountryCodes = locationLookup[item].currentCountryCodes
          countryCodeMatchedIds.push({ countryCode: cc, placeId: item, countryName, currentCountryCodes })
        } else {
          countryCodeMatchedIds.push({ countryCode: cc, placeId: item })
        }
      }
    })

    if (countryCodeMatchedIds.length === 0 || tree.length <= 1) {
      dispatchInitialTreelessStateUpdate(true)
    } else {
      const countryPercentages = LocationHelper.getAncestryPercentages(tree, countryCodeMatchedIds)
      dispatch({
        type: ACTIONS.UPDATE_INITIAL_STATE,
        data: {
          hasMappableTree: true,
          hasUpdatedInitialState: true,
          tree,
          countryPercentages,
          countryCodeMatchedIds,
          locationLookup,
          personId: user?.personId,
        },
      })
    }
  }

  React.useEffect(() => {
    if (user?.signedIn && user.personId) {
      makeRequest({
        successCB: (r) => {
          const locationLookup = r.data.placesLookup
          r.data.persons.forEach((curPerson) => {
            const expandedPerson = LocationHelper.expandPerson({
              person: curPerson,
              locationLookup,
              tree: r.data.persons,
            })

            workingTree.push(expandedPerson)
          })
          getHeritagePercentage(workingTree, locationLookup)
        },
        failureCB: () => {
          dispatch({
            type: ACTIONS.UPDATE_INITIAL_STATE,
            data: {
              hasMappableTree: false,
              hasUpdatedInitialState: true,
              hasFamilyDataError: true,
              tree: [],
              countryPercentages: [],
            },
          })
        },
      })
    } else if (!user?.userLoading) {
      dispatchInitialTreelessStateUpdate()
    }
  })
}

export { WcfProvider, useWcfState, useWcfInitialization, useWcfDispatch, ACTIONS }
